import { Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Child, StudentSchoolRelation } from '@schooly/api';
import { RELATIONSHIP_TYPE_OPTIONS } from '@schooly/constants';
import { DROPDOWN, ELEMENT } from '@schooly/constants/test-ids';
import { Loading, LockIcon, MinusIcon, StarIcon } from '@schooly/style';

import { FormTagSelect } from 'apps/web/src/components/ui/Input/FormTagSelect';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isTempUserId } from '../CreatePersonForm/utils';
import PersonCardSelectable from '../PersonCard/PersonCardSelectable';
import { PersonCardIconButton } from '../PersonCard/PersonCardSelectable.styled';
import { EditFamilyCardWrapper } from './EditFamilyContent.styled';
import { EditFamilyForm } from './EditFamilyModal';

interface EditFamilyContentProps {
  user?: Child | StudentSchoolRelation;
  parentForm: EditFamilyForm[];
  isLoading?: boolean;
  onPrimaryContactIdChange: (index: number) => void;
  onParentDelete: (index: number, isPrimary: boolean) => void;
}

export const EditFamilyContent: React.FC<EditFamilyContentProps> = ({
  user,
  parentForm,
  onParentDelete,
  isLoading,
  onPrimaryContactIdChange,
}) => {
  const { formatMessage } = useIntl();
  function renderParents() {
    if (isLoading) return <Loading />;
    if (!parentForm.length) {
      return (
        <Stack p={3} justifyContent="center" alignItems="center">
          <Typography align="center" color="text.secondary">
            <FormattedMessage id="people-SelectOrCreateAdults" />
          </Typography>
        </Stack>
      );
    }

    const canBeEdited = Boolean((user as StudentSchoolRelation)?.can_be_edited);

    const renderCardIcon = (onDelete: () => void) => {
      if (!canBeEdited) return null;

      if (parentForm.length === 1)
        return (
          <Tooltip describeChild title={formatMessage({ id: 'people-CannotRemoveLastParent' })}>
            <IconButton
              sx={(theme) => ({ width: theme.spacing(3), height: theme.spacing(3) })}
              inverse
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        );

      return (
        <PersonCardIconButton onClick={onDelete}>
          <MinusIcon />
        </PersonCardIconButton>
      );
    };

    return (
      <Stack>
        {parentForm.map((data, i) => {
          const isPrimaryContact = data.primary;
          const createdParent = isTempUserId(data.parent.user_id);

          return (
            <EditFamilyCardWrapper key={data.parent.user_id} data-test-id={ELEMENT.ROW}>
              <PersonCardSelectable
                user={data.parent}
                userType="adult"
                isUsernameLinkView
                isUsernameClickable={!createdParent}
                renderCustomIcon={renderCardIcon(() => onParentDelete(i, Boolean(data.primary)))}
                isListItem
              >
                <Stack direction="row" gap={1} flex="1 1 200%" mr={1}>
                  <Stack direction="row" alignItems="center" flex="1 0 35%">
                    <FormTagSelect
                      name={`parentsForm.${i}.relation_type`}
                      options={RELATIONSHIP_TYPE_OPTIONS}
                      testId={DROPDOWN.RELATIONSHIP}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    className="primaryContact"
                    flex="1 1 30%"
                    gap={0.5}
                    sx={(theme) => ({
                      visibility: isPrimaryContact ? 'visible' : 'hidden',
                      cursor: 'pointer',
                      color: isPrimaryContact
                        ? theme.palette.common.grey2
                        : theme.palette.common.grey,

                      '&:hover': {
                        color: theme.palette.common.grey2,
                      },
                    })}
                    onClick={() => {
                      onPrimaryContactIdChange(i);
                    }}
                  >
                    <Icon fontSize="small">
                      <StarIcon />
                    </Icon>
                    <Typography
                      sx={{
                        display: '-webkit-box',
                        '-webkit-box-orient': 'vertical',
                        '-webkit-line-clamp': '2',
                        overflowX: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {isPrimaryContact ? (
                        <FormattedMessage id="people-PrimaryContact" />
                      ) : (
                        <FormattedMessage id="people-MakePrimaryContact" />
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </PersonCardSelectable>
            </EditFamilyCardWrapper>
          );
        })}
      </Stack>
    );
  }

  return renderParents();
};
